import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Container, Row, Col, Card, Badge, Form, Button } from 'react-bootstrap';
import { apiRequests } from '../../shared/api/apiRequests';
import { dateToString } from '../../shared/utils/parseDate';
import { toast } from 'react-toastify';

// Компонент для просмотра и редактирования отдельной заявки
const ViewSummaryPage = observer(() => {
  const { id: vacancyId, summaryId } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [vacancy, setVacancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    status: 1,
    note: ''
  });

  // Статусы заявок с переводом и цветом
  const statusMap = {
    1: { name: 'Активная', color: 'primary' },
    2: { name: 'Кандидат', color: 'info' },
    3: { name: 'Интервью', color: 'warning' },
    4: { name: 'Нанят', color: 'success' },
    5: { name: 'Не нанят', color: 'danger' },
    6: { name: 'Резерв', color: 'secondary' },
    7: { name: 'Отклонен', color: 'dark' },
  };

  // Загрузка данных заявки
  const getSummaryData = async () => {
    try {
      setLoading(true);
      // Получение данных заявки
      const summaryData = await apiRequests.hr.getSummary(summaryId);
      setSummary(summaryData);
      setForm({
        status: summaryData.status,
        note: summaryData.note || ''
      });
      
      // Получение данных вакансии
      const vacancyData = await apiRequests.hr.getById(vacancyId);
      setVacancy(vacancyData);
    } catch (error) {
      console.error('Ошибка при получении данных заявки:', error);
      toast.error('Не удалось загрузить информацию о заявке');
    } finally {
      setLoading(false);
    }
  };

  // Обработка изменения заявки
  const handleSaveSummary = async () => {
    try {
      // Создаем объект для отправки на сервер
      const updatedData = {
        id: summary.id,
        status: parseInt(form.status),
        note: form.note
      };
      
      console.log('Данные для обновления заявки:', updatedData);
      
      // Отправляем запрос на обновление заявки
      await apiRequests.hr.updateSummary(updatedData);
      
      toast.success('Статус заявки успешно обновлен');
      setEditMode(false);
      getSummaryData(); // Перезагружаем данные
    } catch (error) {
      console.error('Ошибка при обновлении заявки:', error);
      toast.error('Не удалось обновить статус заявки');
    }
  };

  useEffect(() => {
    getSummaryData();
  }, [summaryId]);

  if (loading) {
    return (
      <Container fluid className="py-5">
        <Row>
          <Col className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Загрузка...</span>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0">
              Заявка от {summary?.name} {summary?.surname}
            </h2>
            <div>
              <Link to={`/HR/vacancies/${vacancyId}/summaries`} className="btn btn-outline-primary me-2">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 12L5 12" stroke="#0d6efd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 19L5 12L12 5" stroke="#0d6efd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="ms-2">К списку заявок</span>
              </Link>
              {!editMode ? (
                <Button variant="success" onClick={() => setEditMode(true)}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M17.204 10.796L19 9C19.5453 8.45475 19.8179 8.18213 19.9636 7.88803C20.2409 7.32848 20.2409 6.67152 19.9636 6.11197C19.8179 5.81788 19.5453 5.54525 19 5C18.4548 4.45475 18.1821 4.18213 17.888 4.03639C17.3285 3.75911 16.6715 3.75911 16.112 4.03639C15.8179 4.18213 15.5453 4.45475 15 5L13.1814 6.81866C14.1452 8.46926 15.5314 9.84482 17.204 10.796ZM11.7269 8.27311L4.8564 15.1436C4.43134 15.5687 4.21881 15.7812 4.07907 16.0423C3.93934 16.3034 3.88039 16.5981 3.7625 17.1876L3.1471 20.2646C3.08058 20.5972 3.04732 20.7635 3.14193 20.8581C3.23654 20.9527 3.40284 20.9194 3.73545 20.8529L6.81243 20.2375C7.40189 20.1196 7.69661 20.0607 7.95771 19.9209C8.21881 19.7812 8.43134 19.5687 8.8564 19.1436L15.7458 12.2542C14.1241 11.2386 12.7524 9.87627 11.7269 8.27311Z" fill="#FFFFFF"/>
                  </svg>
                  <span className="ms-2">Редактировать</span>
                </Button>
              ) : (
                <div>
                  <Button variant="secondary" className="me-2" onClick={() => setEditMode(false)}>
                    Отмена
                  </Button>
                  <Button variant="success" onClick={handleSaveSummary}>
                    Сохранить
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={8}>
          <div className="page-table-element">
            <Card.Body>
              <Row>
                <Col md={6}>
                  <dl>
                    <dt>Имя</dt>
                    <dd>{summary?.name || 'Не указано'}</dd>
                    
                    <dt>Фамилия</dt>
                    <dd>{summary?.surname || 'Не указано'}</dd>
                    
                    <dt>Дата создания</dt>
                    <dd>{dateToString(summary?.createdAt)}</dd>
                  </dl>
                </Col>
                <Col md={6}>
                  <dl>
                    <dt>Вакансия</dt>
                    <dd>
                      <Link to={`/HR/vacancies/${vacancyId}`}>{vacancy?.title}</Link>
                    </dd>
                    
                    {summary?.cvPath && (
                      <>
                        <dt>Резюме</dt>
                        <dd>
                          <a href={summary.cvPath} className="btn btn-sm btn-outline-primary" target="_blank" rel="noopener noreferrer">
                            Скачать резюме
                          </a>
                        </dd>
                      </>
                    )}
                    
                    <dt>Идентификатор</dt>
                    <dd>{summary?.createdByFingerprint || 'Не определен'}</dd>
                  </dl>
                </Col>
              </Row>
              
              <div className="mt-3">
                <h5>О кандидате</h5>
                <p className="mb-0">
                  {summary?.aboutInfo || 'Информация не предоставлена'}
                </p>
              </div>
            </Card.Body>
          </div>
        </Col>
        
        <Col md={4}>
          <div className="page-table-element">
            <Card.Body>
              {editMode ? (
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Статус</Form.Label>
                    <Form.Select 
                      value={form.status} 
                      onChange={(e) => setForm({...form, status: e.target.value})}
                    >
                      {Object.entries(statusMap).map(([key, value]) => (
                        <option key={key} value={key}>{value.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Примечание</Form.Label>
                    <Form.Control 
                      as="textarea" 
                      rows={5} 
                      placeholder="Добавьте заметку о кандидате"
                      value={form.note}
                      onChange={(e) => setForm({...form, note: e.target.value})}
                    />
                  </Form.Group>
                </Form>
              ) : (
                <>
                  <div className="mb-3">
                    <h5 className="mb-2">Текущий статус</h5>
                    <Badge bg={statusMap[summary?.status]?.color || 'primary'} className="fs-6">
                      {statusMap[summary?.status]?.name || 'Активная'}
                    </Badge>
                  </div>
                  
                  <div>
                    <h5 className="mb-2">Примечание</h5>
                    <p className="mb-0">{summary?.note || 'Примечаний нет'}</p>
                  </div>
                </>
              )}
            </Card.Body>
          </div>
        </Col>
      </Row>
    </Container>
  );
});

export { ViewSummaryPage };
